import React, {
  ForwardRefRenderFunction,
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  useEffect,
} from "react";

import { Tooltip, Box, CircularProgress } from "@mui/material";
import { Link, Message, Notification, Sms, Clock } from "iconsax-react";
import { useTranslation } from "react-i18next";
import { FiCheck } from "react-icons/fi";

import { Color } from "../../Form/Button";
import Button from "../../Form/Button";
import { ActionButtonProps } from "../ActionButtons";
import { Customer } from "../../../models/customer";
import { Job } from "../../../models/job";
import { Sales } from "../../../models/sales";
import { Shipment } from "../../../models/shipments";
import { InvoicesCharges } from "../../../models/invoice.charges";
import { PurchaseOrder } from "../../../models/purchase.order";
import { ShareOptions, UsedInEmailTemplates } from "../../../util/enums";
import EmailEditor, { EmailEditorHandle } from "./components/EmailEditor";
import SMSEditor, { SMSEditorHandle } from "./components/SMSEditor";
import PushNotificationEditor, {
  PushNotificationEditorHandle,
} from "./components/PushNotificationEditor";
import {
  Card,
  CardBody,
  CardLoading,
  CardText,
  CardTitle,
  CheckmarkContainer,
  IconContainer,
  LoadingContainer,
  StylishModal,
  Wrapper,
  WrapperCardBody,
} from "./styles";
import ScheduleSendModal from "./components/ScheduleSendModal";

// Define entity-specific types
type QuoteContext = {
  entityType: "quote";
  entityId: number;
  entity: Job | Sales;
  customer?: Customer;
};

type OrderContext = {
  entityType: "order";
  entityId: number;
  entity: Job | Sales | PurchaseOrder;
  customer?: Customer;
};

type ShipmentContext = {
  entityType: "shipment";
  entityId: number;
  entity: Shipment;
  customer?: Customer;
};

type InvoiceContext = {
  entityType: "invoice";
  entityId: number;
  entity: InvoicesCharges;
  customer?: Customer;
};

export type EntityContext = QuoteContext | OrderContext | ShipmentContext | InvoiceContext;

interface SendCommunicationModalProps {
  onRefetch?: () => void;
  onSendCustomerPortalLink?: (entityId: number) => void;
  isSendingCustomerPortalLink?: boolean;
  usedIn?: UsedInEmailTemplates;
  job?: Job | Sales;
  customer?: Customer;
}

export interface SendCommunicationModalHandle {
  onOpen: (context: EntityContext) => void;
}

const SendCommunicationModal: ForwardRefRenderFunction<
  SendCommunicationModalHandle,
  SendCommunicationModalProps
> = (props, ref) => {
  const {
    onRefetch,
    onSendCustomerPortalLink,
    isSendingCustomerPortalLink = false,
    usedIn = UsedInEmailTemplates.ORDERS,
    job: propJob,
    customer: propCustomer,
  } = props;

  const { t: translate } = useTranslation();
  const emailEditorRef = useRef<EmailEditorHandle>(null);
  const smsEditorRef = useRef<SMSEditorHandle>(null);
  const pushNotificationEditorRef = useRef<PushNotificationEditorHandle>(null);

  const [open, setOpen] = useState<boolean>(false);
  const [entityContext, setEntityContext] = useState<EntityContext | undefined>();
  const [selectedOption, setSelectedOption] = useState<string>("");
  const [currentStep, setCurrentStep] = useState<number>(0); // 0 = options selection, 1 = email editor
  const [scheduleModalOpen, setScheduleModalOpen] = useState<boolean>(false);
  const [isSendingCommunication, setIsSendingCommunication] = useState<boolean>(false);

  useImperativeHandle(ref, () => ({
    onOpen: onOpenModal,
  }));

  useEffect(() => {
    if (currentStep === 1) {
      const checkLoading = () => {
        if (selectedOption === ShareOptions.SHARE_EMAIL && emailEditorRef.current) {
          setIsSendingCommunication(emailEditorRef.current.isLoading);
        } else if (selectedOption === ShareOptions.SHARE_SMS && smsEditorRef.current) {
          setIsSendingCommunication(smsEditorRef.current.isSending);
        } else if (
          selectedOption === ShareOptions.SHARE_PUSH_NOTIFICATION &&
          pushNotificationEditorRef.current
        ) {
          setIsSendingCommunication(pushNotificationEditorRef.current.isSending);
        }
      };

      const interval = setInterval(checkLoading, 100);
      return () => clearInterval(interval);
    } else {
      setIsSendingCommunication(false);
    }
  }, [currentStep, selectedOption]);

  const onOpenModal = (context: EntityContext) => {
    setOpen(true);
    setEntityContext(context);
    setCurrentStep(0);
    setSelectedOption("");
  };

  const isSaveButtonDisabled =
    (currentStep === 0 && !selectedOption.trim().length) ||
    isSendingCustomerPortalLink ||
    isSendingCommunication;

  const actionButtonProps: ActionButtonProps = {
    dataCySave: "submitButton",
    dataCyCancel: "cancelButton",
    textSave:
      currentStep === 0 ? (
        translate("share")
      ) : isSendingCommunication ? (
        <CircularProgress size={20} color="inherit" />
      ) : (
        translate("send")
      ),
    textCancel: currentStep === 0 ? translate("cancel") : translate("back"),
    customColorSave: Color.SUCCESS,
    customColorCancel: currentStep === 0 ? Color.CANCEL : Color.TEXT_INFO,
    saveDisabled: isSaveButtonDisabled,
    onSave: () => onSaveAction(),
    onCancel: () => {
      if (currentStep === 1) {
        setCurrentStep(0);
        setSelectedOption("");
      } else {
        setOpen(false);
        setSelectedOption("");
        setCurrentStep(0);
      }
    },
  };

  const onSaveAction = () => {
    if (currentStep === 0) {
      // Step 0: Handle option selection
      if (selectedOption === ShareOptions.SHARE_CUSTOMER_PORTAL_LINK) {
        if (onSendCustomerPortalLink && entityContext) {
          return onSendCustomerPortalLink(entityContext.entityId);
        }
        return;
      }

      if (selectedOption === ShareOptions.SHARE_EMAIL) {
        // Transition to email editor step
        setCurrentStep(1);
        return;
      }

      if (selectedOption === ShareOptions.SHARE_SMS) {
        // Transition to SMS editor step
        setCurrentStep(1);
        return;
      }

      if (selectedOption === ShareOptions.SHARE_PUSH_NOTIFICATION) {
        // Transition to Push Notification editor step
        setCurrentStep(1);
        return;
      }
    } else if (currentStep === 1) {
      // Step 1: Trigger send from appropriate editor
      if (selectedOption === ShareOptions.SHARE_EMAIL) {
        emailEditorRef.current?.sendEmail();
      } else if (selectedOption === ShareOptions.SHARE_SMS) {
        smsEditorRef.current?.sendSMS();
      } else if (selectedOption === ShareOptions.SHARE_PUSH_NOTIFICATION) {
        pushNotificationEditorRef.current?.sendPushNotification();
      }
    }

    return null;
  };

  const shareOptions = [
    {
      icon: <Link size="24" variant="Outline" />,
      key: ShareOptions.SHARE_CUSTOMER_PORTAL_LINK,
      title: translate("sendCustomerPortalLink"),
      tooltipTitleOnDisabled: "Coming Soon!",
      text: translate("sendTheCustomerPortalLinkWithAccessToTheOrderOnline"),
      isLoading: isSendingCustomerPortalLink,
      disabled: false,
      action: () => setSelectedOption(ShareOptions.SHARE_CUSTOMER_PORTAL_LINK),
    },
    {
      icon: <Message size="24" variant="Outline" />,
      key: ShareOptions.SHARE_EMAIL,
      title: translate("sendEmail"),
      text: translate("writeAnEmailOrUseATemplate"),
      tooltipTitleOnDisabled: translate(""),
      disabled: false,
      action: () => setSelectedOption(ShareOptions.SHARE_EMAIL),
    },
    {
      icon: <Sms size="24" variant="Outline" />,
      key: ShareOptions.SHARE_SMS,
      title: translate("sendSMS") || "Send SMS",
      text: translate("writeASMSOrUseATemplate") || "Write a SMS or use a template",
      tooltipTitleOnDisabled: translate(""),
      disabled: false,
      action: () => setSelectedOption(ShareOptions.SHARE_SMS),
    },
    {
      icon: <Notification size="24" variant="Outline" />,
      key: ShareOptions.SHARE_PUSH_NOTIFICATION,
      title: translate("pushNotification") || "Push Notification",
      text:
        translate("writeAPushNotificationOrUseATemplate") ||
        "Write a push notification or use a template",
      tooltipTitleOnDisabled: translate(""),
      disabled: false,
      action: () => setSelectedOption(ShareOptions.SHARE_PUSH_NOTIFICATION),
    },
  ];

  const renderShareOptions = () => {
    return shareOptions.map((option, index: number) => {
      const isSelected = selectedOption === option.key;
      const iconColor = isSelected ? "#0055A4" : "#1D2939";

      return (
        <Tooltip
          key={`option-${index + 1}`}
          title={option.disabled ? option.tooltipTitleOnDisabled : ""}
          placement="top"
        >
          <Card
            disabled={option.disabled}
            active={isSelected}
            onClick={option.disabled ? undefined : option.action}
          >
            <IconContainer>{React.cloneElement(option.icon, { color: iconColor })}</IconContainer>
            <WrapperCardBody>
              <CardBody>
                <CardTitle active={isSelected} disabled={option.disabled}>
                  {option.title}
                </CardTitle>
                <CardText active={isSelected} disabled={option.disabled}>
                  {option.text}
                </CardText>
              </CardBody>
              {isSelected && (
                <CheckmarkContainer>
                  <FiCheck size={18} color="#0055A4" />
                </CheckmarkContainer>
              )}
            </WrapperCardBody>
            {option?.isLoading && (
              <LoadingContainer>
                <CardLoading size={20} />
              </LoadingContainer>
            )}
          </Card>
        </Tooltip>
      );
    });
  };

  const getModalTitle = () => {
    if (currentStep === 0) {
      return translate("whatDoYouWantToSendToCustomer");
    }
    if (selectedOption === ShareOptions.SHARE_EMAIL) {
      return translate("sendEmail");
    }
    if (selectedOption === ShareOptions.SHARE_SMS) {
      return translate("sendSMS") || "Send SMS";
    }
    if (selectedOption === ShareOptions.SHARE_PUSH_NOTIFICATION) {
      return translate("pushNotification") || "Push Notification";
    }
    return translate("whatDoYouWantToSendToCustomer");
  };

  const handleClose = () => {
    setSelectedOption("");
    setEntityContext(undefined);
    setCurrentStep(0);
    setOpen(false);
    onRefetch?.();
  };

  const handleScheduleSend = () => {
    setScheduleModalOpen(true);
  };

  const handleScheduleConfirm = (scheduledDate: string) => {
    if (selectedOption === ShareOptions.SHARE_EMAIL) {
      emailEditorRef.current?.sendEmail(scheduledDate);
    } else if (selectedOption === ShareOptions.SHARE_SMS) {
      smsEditorRef.current?.sendSMS(scheduledDate);
    } else if (selectedOption === ShareOptions.SHARE_PUSH_NOTIFICATION) {
      pushNotificationEditorRef.current?.sendPushNotification(scheduledDate);
    }
    setScheduleModalOpen(false);
  };

  const getScheduleButton = () => {
    if (currentStep === 1) {
      return (
        <Button
          variant="outlined"
          onClick={handleScheduleSend}
          customColor={Color.SUCCESS}
          startIcon={<Clock size={20} />}
          data-cy="schedule-send-button"
          sx={{ marginLeft: "10px" }}
        >
          {translate("scheduleSend")}
        </Button>
      );
    }
    return null;
  };

  return (
    <>
      <StylishModal
        open={open}
        handleClose={handleClose}
        showActionButtons
        title={getModalTitle()}
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
        actionButtonProps={actionButtonProps}
        customRightButtons={getScheduleButton()}
      >
        {currentStep === 0 ? (
          <Wrapper>{renderShareOptions()}</Wrapper>
        ) : currentStep === 1 && selectedOption === ShareOptions.SHARE_EMAIL && entityContext ? (
          <EmailEditor
            ref={emailEditorRef}
            entityContext={entityContext}
            usedIn={usedIn}
            onSend={() => {
              handleClose();
            }}
            onCancel={() => {
              setCurrentStep(0);
              setSelectedOption("");
            }}
          />
        ) : currentStep === 1 && selectedOption === ShareOptions.SHARE_SMS && entityContext ? (
          <SMSEditor
            ref={smsEditorRef}
            entityContext={entityContext}
            usedIn={usedIn}
            onSend={() => {
              handleClose();
            }}
            onCancel={() => {
              setCurrentStep(0);
              setSelectedOption("");
            }}
          />
        ) : currentStep === 1 &&
          selectedOption === ShareOptions.SHARE_PUSH_NOTIFICATION &&
          entityContext ? (
          <PushNotificationEditor
            ref={pushNotificationEditorRef}
            entityContext={entityContext}
            usedIn={usedIn}
            onSend={() => {
              handleClose();
            }}
            onCancel={() => {
              setCurrentStep(0);
              setSelectedOption("");
            }}
          />
        ) : null}
      </StylishModal>
      {currentStep === 1 && (
        <ScheduleSendModal
          open={scheduleModalOpen}
          onClose={() => setScheduleModalOpen(false)}
          onSchedule={handleScheduleConfirm}
          communicationType={
            selectedOption === ShareOptions.SHARE_EMAIL
              ? "email"
              : selectedOption === ShareOptions.SHARE_SMS
              ? "sms"
              : "push"
          }
        />
      )}
    </>
  );
};

export default forwardRef(SendCommunicationModal);
